// Site layout-specific JavaScript
// This handles the default site layout and its variants (main, blog1, alexa1)

import { ready } from "../modules/core";

// Shared functionality for blog1 and alexa1 layouts
function setupFloatingLabels() {
  console.log("Setting up floating labels with native JavaScript");

  // Use native JavaScript for floating labels (no jQuery)
  document.querySelectorAll('.floating-label-form-group input, .floating-label-form-group textarea').forEach(input => {
    // Check initial state
    if (input.value) {
      input.closest('.floating-label-form-group').classList.add('floating-label-form-group-with-value');
    }

    // Add event listeners
    input.addEventListener('input', function() {
      this.closest('.floating-label-form-group').classList.toggle(
        'floating-label-form-group-with-value',
        !!this.value
      );
    });

    input.addEventListener('focus', function() {
      this.closest('.floating-label-form-group').classList.add('floating-label-form-group-with-focus');
    });

    input.addEventListener('blur', function() {
      this.closest('.floating-label-form-group').classList.remove('floating-label-form-group-with-focus');
    });
  });
}

function setupNavbarScrollEffect() {
  console.log("Setting up navbar scroll effect with native JavaScript");

  // Use native JavaScript for navbar scroll effect
  const MQL = 992;
  if (window.innerWidth > MQL) {
    let previousTop = 0;
    const mainNav = document.getElementById('mainNav');

    if (mainNav) {
      const headerHeight = mainNav.offsetHeight;

      window.addEventListener('scroll', function() {
        const currentTop = window.pageYOffset || document.documentElement.scrollTop;

        // Check if user is scrolling up
        if (currentTop < previousTop) {
          // If scrolling up...
          if (currentTop > 0 && mainNav.classList.contains('is-fixed')) {
            mainNav.classList.add('is-visible');
          } else {
            mainNav.classList.remove('is-visible');
            mainNav.classList.remove('is-fixed');
          }
        } else if (currentTop > previousTop) {
          // If scrolling down...
          mainNav.classList.remove('is-visible');
          if (currentTop > headerHeight && !mainNav.classList.contains('is-fixed')) {
            mainNav.classList.add('is-fixed');
          }
        }
        previousTop = currentTop;
      });
    }
  }
}

// Functions for each layout variant
function loadMainLayout() {
  console.log("Main layout variant loaded");
  
  // Main layout-specific initialization
  setupFloatingLabels();
  setupNavbarScrollEffect();
  
  // Any additional main-specific functionality would go here
}

function loadBlog1Layout() {
  console.log("Blog1 layout variant loaded");
  
  // Blog1 layout-specific initialization
  setupFloatingLabels();
  setupNavbarScrollEffect();
  
  // Initialize Prism.js for code highlighting if available
  if (window.Prism) {
    window.Prism.highlightAll();
  } else {
    console.warn("Prism.js not available - code highlighting will not function");
  }
  
  // Add any additional blog1-specific functionality
  initializeBlog1SocialSharing();
}

function initializeBlog1SocialSharing() {
  // Handle social sharing buttons
  document.querySelectorAll('.blog1-social-share').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      
      const platform = this.getAttribute('data-platform');
      const url = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(document.title);
      
      let shareUrl = '';
      
      switch(platform) {
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`;
          break;
        default:
          return;
      }
      
      window.open(shareUrl, '_blank', 'width=600,height=400');
    });
  });
}

function loadAlexa1Layout() {
  console.log("Alexa1 layout variant loaded");
  
  // Alexa1 layout-specific initialization
  setupFloatingLabels();
  setupNavbarScrollEffect();
  
  // Add any additional alexa1-specific functionality
  initializeAlexa1VoiceInteraction();
}

function initializeAlexa1VoiceInteraction() {
  // Simple placeholder for Alexa integration
  const alexaButton = document.querySelector('.alexa-trigger');
  if (alexaButton) {
    alexaButton.addEventListener('click', function() {
      console.log('Alexa button clicked - would trigger voice interaction here');
      
      // Show a visual indicator
      const indicator = document.createElement('div');
      indicator.className = 'alexa-listening';
      indicator.innerHTML = 'Listening...';
      document.body.appendChild(indicator);
      
      // Remove after 3 seconds
      setTimeout(() => {
        if (indicator.parentNode) {
          indicator.parentNode.removeChild(indicator);
        }
      }, 3000);
    });
  }
}

// Export a function that initializes the site layout
export function initialize() {
  console.log("Site layout initialization function called");
  
  // Common initialization for all site layouts
  
  // Detect which specific site layout variant is being used
  const body = document.querySelector("body");
  
  if (body.classList.contains("layout-blog1") || body.dataset.layout === "blog1") {
    loadBlog1Layout();
  } else if (body.classList.contains("layout-alexa1") || body.dataset.layout === "alexa1") {
    loadAlexa1Layout();
  } else {
    // Default to main layout
    loadMainLayout();
  }
  
  return true;
}

// Clean up navbar scroll effect state before Turbo caches the page
// This prevents the navbar from being cached in a hidden/transformed state
function cleanupNavbarForCache() {
  const mainNav = document.getElementById('mainNav');
  if (mainNav) {
    mainNav.classList.remove('is-fixed', 'is-visible');
  }

  // Also reset Bootstrap collapse state
  const navbarCollapse = document.getElementById('navbarResponsive');
  if (navbarCollapse) {
    navbarCollapse.classList.remove('show', 'collapsing');
    navbarCollapse.classList.add('collapse');
    navbarCollapse.style.height = '';

    const toggler = document.querySelector('.navbar-toggler');
    if (toggler) {
      toggler.setAttribute('aria-expanded', 'false');
      toggler.classList.add('collapsed');
    }
  }
}

// Register the cleanup handler once
if (typeof window !== 'undefined' && !window._navbarCacheCleanupRegistered) {
  document.addEventListener('turbo:before-cache', cleanupNavbarForCache);
  document.addEventListener('turbo:render', cleanupNavbarForCache);
  document.addEventListener('turbo:load', cleanupNavbarForCache);
  window._navbarCacheCleanupRegistered = true;
}

// Also initialize on page load using the ready function
ready(() => {
  console.log("Site layout loaded via ready function");

  // We don't need jQuery anymore as we're using native browser APIs and Stimulus
  // This verification is no longer needed since we have our stubs

  initialize();
});

// Export the module name for easier debugging
export const MODULE_NAME = 'site';

// Export layout variants for direct access
export const variants = {
  main: loadMainLayout,
  blog1: loadBlog1Layout,
  alexa1: loadAlexa1Layout
};

// Default export for dynamic imports
export default {
  initialize,
  MODULE_NAME,
  variants
};